import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useChatWebSocket } from './useChatWebSocket';

const API = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

// ── Tick (read receipts) ─────────────────────────────────────────────────────
function TickPath({ color }) {
  return (
    <svg width={13} height={13} viewBox="0 0 14 14" fill="none" style={{ display:'block', flexShrink:0 }}>
      <polyline points="2,8 5.5,11 12,3.5" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}
function Tick({ status }) {
  if (!status) return null;
  const color  = status === 'read' ? '#3BA3FF' : '#A0A0A0';
  const double = status !== 'sent';
  return (
    <span style={{ display:'inline-flex', alignItems:'center', flexShrink:0 }}>
      <TickPath color={color}/>
      {double && <span style={{ display:'inline-flex', marginLeft:-7 }}><TickPath color={color}/></span>}
    </span>
  );
}

// ── Presence ─────────────────────────────────────────────────────────────────
function getPresenceText(presence) {
  if (!presence || presence.status === 'online') return { text:'Active Now', color:'#22c55e' };
  if (presence.lastSeen) {
    const d    = new Date(presence.lastSeen);
    const diff = Math.floor((new Date() - d) / 86400000);
    if (diff === 0) return { text:'Active Today',     color:'#22c55e' };
    if (diff === 1) return { text:'Active Yesterday', color:'#f59e0b' };
    if (diff <= 7)  return { text:`Active ${diff} days ago`, color:'#9ca3af' };
    return { text:d.toLocaleDateString(), color:'#9ca3af' };
  }
  return { text:'Recently Active', color:'#9ca3af' };
}

// ── Helpers ───────────────────────────────────────────────────────────────────
export const formatFullNameToShort = (first, last, fallback) => {
  let raw = (first && last) ? `${first} ${last}` : first || fallback || 'User';
  let base = String(raw).split('@')[0].replace(/([a-z])([A-Z])/g,'$1 $2');
  const parts = base.split(/[._\-\s]/).filter(Boolean);
  if (parts.length > 1) {
    return `${parts[0][0].toUpperCase()}${parts[0].slice(1).toLowerCase()} ${parts[parts.length-1][0].toUpperCase()}.`;
  }
  return parts[0] ? `${parts[0][0].toUpperCase()}${parts[0].slice(1).toLowerCase()}` : 'User';
};
export const getInitials = (name) => {
  if (!name) return 'U';
  const p = name.trim().split(' ');
  return p.length > 1 ? (p[0][0]+p[1][0]).toUpperCase() : name.substring(0,2).toUpperCase();
};

// ── Message Bubble ────────────────────────────────────────────────────────────
function Bubble({ msg, isMe, D }) {
  const time   = msg.created_at ? new Date(msg.created_at).toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'}) : 'Just now';
  let content  = msg.content || msg.text || '';
  if (typeof content === 'object') content = content.text || content.message || JSON.stringify(content);

  return (
    <div style={{ display:'flex', justifyContent:isMe?'flex-end':'flex-start', marginBottom:6,
      paddingLeft:isMe?48:0, paddingRight:isMe?0:48,
      animation:'fadeInUp 0.3s ease-out forwards' }}>
      <style>{`@keyframes fadeInUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}`}</style>
      <div style={{
        maxWidth:'74%', minWidth:0, wordBreak:'break-word', overflowWrap:'break-word',
        background: isMe ? (D?'#2563eb':'#0f172a') : (D?'#1e293b':'#ffffff'),
        color: isMe ? '#ffffff' : (D?'#f0f4f8':'#111827'),
        borderRadius: isMe?'18px 18px 4px 18px':'18px 18px 18px 4px',
        padding:'9px 12px 6px', boxShadow:'0 1px 2px rgba(0,0,0,0.1)',
        border: isMe ? 'none' : `1px solid ${D?'rgba(255,255,255,0.10)':'#e5e7eb'}`,
      }}>
        <p style={{ margin:0, fontSize:14, lineHeight:1.55 }}>{content}</p>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-end', gap:3, marginTop:4 }}>
          <span style={{ fontSize:10, color:isMe?'rgba(255,255,255,0.5)':'#9ca3af', whiteSpace:'nowrap' }}>{time}</span>
          {isMe && <Tick status={msg.status || 'read'}/>}
        </div>
      </div>
    </div>
  );
}

// ── Main ChatWindow ───────────────────────────────────────────────────────────
export const ChatWindow = ({ activeConvo, currentUser, isDark }) => {
  const D = !!isDark;
  const [inputText,  setInputText]  = useState('');
  const [sendError,  setSendError]  = useState(null);
  const messagesEndRef = useRef(null);

  const { messages, setMessages, isConnected, sendMessage, peerPresence } =
    useChatWebSocket(activeConvo?.id, currentUser);

  useEffect(() => {
    if (!activeConvo?.id) return;
    let cancelled = false;
    fetch(`${API}/api/tradelink/chat/${activeConvo.id}/history/`, {
      headers: { Authorization: `Token ${localStorage.getItem('user_token')}` },
    })
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        const raw = Array.isArray(data) ? data : (data.results ?? []);
        setMessages([...raw].reverse().map(m => ({ ...m, status:'read' })));
      })
      .catch(console.error);
    return () => { cancelled = true; };
  }, [activeConvo?.id, setMessages]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior:'smooth' }); }, [messages]);

  const handleSend = useCallback((e) => {
    if (e) e.preventDefault();
    const text = inputText.trim();
    if (!text) return;
    if (!isConnected) {
      setSendError('Not connected — will retry when reconnected.');
      setTimeout(() => setSendError(null), 3500);
      return;
    }
    setSendError(null);
    const tempMsg = {
      id:`temp_${Date.now()}`, content:text,
      sender_id: currentUser?.id || currentUser?.pk,
      sender_name: currentUser?.username || currentUser?.email,
      created_at: new Date().toISOString(), status:'sent',
    };
    setMessages(prev => [...prev, tempMsg]);
    setInputText('');
    if (!sendMessage(text)) {
      setMessages(prev => prev.filter(m => m.id !== tempMsg.id));
      setInputText(text);
      setSendError('Send failed — please try again.');
      setTimeout(() => setSendError(null), 3500);
    }
  }, [inputText, isConnected, sendMessage, currentUser, setMessages]);

  if (!activeConvo) return (
    <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
      background:D?'#0f172a':'#efeae2', color:'#9ca3af', gap:10 }}>
      <div style={{ fontSize:44 }}>💬</div>
      <div style={{ fontWeight:700, fontSize:16, color:D?'#94a3b8':'#64748b' }}>Trade Link Chat</div>
      <div style={{ fontSize:13 }}>Select a conversation to start</div>
    </div>
  );

  const isMe = (msg) => {
    if (!currentUser) return false;
    const uid = currentUser.id || currentUser.pk || currentUser.user_id;
    const uname = currentUser.username || currentUser.email;
    return (uid && (msg.sender === uid || msg.sender_id === uid)) ||
           (uname && (msg.sender_name === uname || msg.sender === uname));
  };

  const unique = messages.filter((m, i, self) =>
    i === self.findIndex(x =>
      x.id === m.id ||
      (x.content === m.content && Math.abs(new Date(x.created_at) - new Date(m.created_at)) < 5000)
    )
  );

  const partnerName = formatFullNameToShort(activeConvo.partner_first_name, activeConvo.partner_last_name,
    activeConvo.partner_name || activeConvo.partner_email);
  const presence    = getPresenceText(peerPresence);

  const bg     = D ? '#0f172a' : '#efeae2';
  const hdrBg  = D ? '#1e293b' : '#0f172a';
  const inpBg  = D ? '#1e293b' : '#ffffff';
  const inpBdr = D ? '#334155' : '#e5e7eb';
  const sendBg = D ? '#2563eb' : '#0f172a';

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', background:bg, minWidth:0 }}>

      {/* Header */}
      <div style={{ background:hdrBg, padding:'11px 14px', display:'flex', alignItems:'center', gap:10,
        flexShrink:0, boxShadow:'0 1px 4px rgba(0,0,0,0.2)' }}>
        <div style={{ width:38, height:38, borderRadius:'50%', flexShrink:0,
          background:'rgba(14,165,233,0.20)', border:'2px solid rgba(14,165,233,0.40)',
          display:'flex', alignItems:'center', justifyContent:'center',
          fontWeight:800, fontSize:13, color:'#0ea5e9' }}>
          {getInitials(partnerName)}
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontWeight:700, fontSize:15, color:'#fff', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
            {partnerName}
          </div>
          <div style={{ fontSize:11, marginTop:2, color:presence.color, fontWeight:500 }}>
            {presence.text}
          </div>
        </div>
        <div style={{ fontSize:11, fontWeight:600, padding:'3px 9px', borderRadius:99,
          background:isConnected?'rgba(34,197,94,0.15)':'rgba(156,163,175,0.15)',
          color:isConnected?'#22c55e':'#9ca3af', border:`1px solid ${isConnected?'rgba(34,197,94,0.30)':'rgba(156,163,175,0.30)'}` }}>
          {isConnected ? '● Live' : '○ Offline'}
        </div>
      </div>

      {sendError && (
        <div style={{ background:'#fef2f2', border:'1px solid #f87171', borderRadius:8,
          padding:'6px 14px', margin:'4px 12px 0', fontSize:12, color:'#991b1b', fontWeight:600, textAlign:'center' }}>
          ⚠️ {sendError}
        </div>
      )}

      {/* Messages */}
      <div style={{ flex:1, overflowY:'auto', padding:12, display:'flex', flexDirection:'column', gap:2 }}>
        {unique.length === 0 && (
          <div style={{ textAlign:'center', color:'#9ca3af', fontSize:13, marginTop:40 }}>No messages yet. Say hello!</div>
        )}
        {unique.map((msg, idx) => (
          <Bubble key={msg.id || `tmp-${idx}`} msg={msg} isMe={isMe(msg)} D={D}/>
        ))}
        <div ref={messagesEndRef}/>
      </div>

      {/* Input */}
      <form onSubmit={handleSend} style={{ background:inpBg, borderTop:`1px solid ${inpBdr}`,
        padding:'9px 12px', display:'flex', gap:9, alignItems:'flex-end', flexShrink:0 }}>
        <input type="text" value={inputText} onChange={e=>setInputText(e.target.value)}
          placeholder="Type a message…"
          style={{ flex:1, minWidth:0, background:D?'#0f172a':'#f7f8fa',
            border:`1.5px solid ${inpBdr}`, borderRadius:22,
            padding:'9px 13px', color:D?'#f0f4f8':'#111827', fontSize:14, outline:'none' }}
          onFocus={e=>e.target.style.borderColor='#0891b2'}
          onBlur={e=>e.target.style.borderColor=inpBdr}
        />
        <button type="submit" disabled={!inputText.trim()}
          style={{ width:42, height:42, borderRadius:'50%', border:'none',
            cursor:inputText.trim()?'pointer':'not-allowed',
            background:inputText.trim()?sendBg:'#e5e7eb',
            color:'#fff', fontSize:16, flexShrink:0, transition:'background 0.15s' }}>
          ➤
        </button>
      </form>
    </div>
  );
};
